import { __ } from "@wordpress/i18n";
import React, { useEffect } from "react";
import AdvancedControls from "../../../components/common/AdvancedControls";
import InspectorTabs, {
	AdvancedInspectorControls,
	GeneralInspectorControls,
	StyleInspectorControls,
} from "../../../components/common/InspectorTabs";
import Panel from "../../../components/common/Panel";
import ResponsiveControls from "../../../components/common/ResponsiveControls";
import { toggleButtonPresetStyles } from "../../../styles/styles";
import {
	Background,
	Border,
	BoxShadow,
	ColorPicker,
	Dimensions,
	Input,
	PopoverDrawer,
	Select,
	Slider,
	Toggle,
	ToggleButton,
	ToggleButtonGroup,
	Typography,
} from "../../../controls";
import SidebarDrawer from "../../../components/sidebar-drawer/SidebarDrawer";
import {
	LayoutAlignBottom,
	LayoutAlignTop,
	PostVideoPreset1,
	PostVideoPreset2,
	PostVideoPreset3,
} from "../../../components/icons/Icons";
import { Tab, TabList, TabPanel, TabPanels, Tabs } from "@chakra-ui/react";
import ZStack from "../../../components/common/ZStack";
import Reset from "../../../controls/common/Reset";

type Props = {
	attributes: any;
	setAttributes: (attributes: any) => void;
	categories?: any[];
	tags?: any[];
	authorOptions?: any[];
	postTypeOptions?: any[];
};

const InspectorControls: React.ComponentType<Props> = (props) => {
	const {
		attributes: {
			columnGap,
			category,
			tag,
			postCount,
			offset,
			radius,
			column,

			postType,

			presets,
			enableTitle,
			postTitleMarkup,
			enableDate,
			enableExcerpt,
			excerptLimit,
			enableReadMore,
			enableCategory,
			enableCategoryForHighlightPost,
			enableAuthor,
			readMoreText,

			enableMetaSeparator,
			separatorType,
			enableIcon,

			contentPadding,
			headerMetaTypography,

			iconSize,
			iconColor,
			iconBackground,
			iconBorder,

			featuredImageHeight,
			featuredImageWidth,

			blockPadding,
			blockMargin,
			hideOnDesktop,
			hideOnTablet,
			hideOnMobile,

			highlightedPostTitleTypography,
			highlightedPostWidth,
			highlightedPostHeight,
			highlightedPostTitleColor,
			highlightedPostTitleHoverColor,
			highlightedPostContentPadding,
			highlightedPostBoxOverlay,

			postTitleColor,
			postTitleHoverColor,
			postTitleTypography,
			postBoxWidth,
			postBoxOverlay,

			categoryColor,
			categoryHoverColor,
			categoryBackground,
			categoryHoverBackground,
			categoryPadding,
			enableCategoryBorder,
			categoryBorder,
			categoryBoxShadow,
			categoryHoverBorder,
			categoryBoxShadowHover,

			metaLinkColor,
			metaLinkHoverColor,
			metaIconColor,
			metaTypography,
			metaSeparatorColor,
			metaPosition,

			excerptColor,
			excerptMargin,

			readMoreColor,
			readMoreHoverColor,
			readMoreBackground,
			readMoreHoverBackground,
			readMoreSpacing,
			enableReadMoreBorder,
			readMorePadding,
			readMoreBorder,
			readMoreBoxShadow,
			readMoreHoverBorder,
			readMoreBoxShadowHover,
		},
		setAttributes,
		categories,
		tags,
		postTypeOptions,
	} = props;

	useEffect(() => {
		const type = wp.data.select("core/editor").getCurrentPost().type;

		if (type === "mzb-builder-template") {
			setAttributes({ postType: "mzb-builder" });
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return (
		<>
			<InspectorTabs />
			<GeneralInspectorControls>
				<Panel title={__("General", "magazine-blocks")} initialOpen>
					{!presets && (
						<Select
							value={column}
							onChange={(val) => setAttributes({ column: val })}
							label={__("Column", "magazine-blocks")}
							options={[
								{
									label: __("1", "magazine-blocks"),
									value: "1",
								},
								{
									label: __("2", "magazine-blocks"),
									value: "2",
								},
								{
									label: __("3", "magazine-blocks"),
									value: "3",
								},
							]}
						/>
					)}

					<SidebarDrawer label={__("Presets", "magazine-blocks")}>
						<ToggleButtonGroup
							responsive={false}
							value={presets}
							onChange={(val) =>
								setAttributes({
									presets: val,
								})
							}
							label={__("Preset", "magazine-blocks")}
							groupProps={toggleButtonPresetStyles}
						>
							{[
								{
									label: __("Style 1", "magazine-blocks"),
									value: "style-1",
									icon: PostVideoPreset1,
								},
								{
									label: __("Style 2", "magazine-blocks"),
									value: "style-2",
									icon: PostVideoPreset2,
								},
								{
									label: __("Style 3", "magazine-blocks"),
									value: "style-3",
									icon: PostVideoPreset3,
								},
							].map((item) => (
								<ToggleButton
									key={item.value}
									value={item.value}
								>
									<item.icon />
									{
										// @ts-ignore
										item.label
									}
								</ToggleButton>
							))}
						</ToggleButtonGroup>
					</SidebarDrawer>
				</Panel>
				<Panel title={__("Query", "magazine-blocks")}>
					{postType !== "mzb-builder" && (
						<>
							<Select
								value={category}
								onChange={(val) =>
									setAttributes({ category: val })
								}
								label={__("Category", "magazine-blocks")}
								options={[
									{ value: "all", label: __("All") },
									...(categories ?? [])?.map(
										({ name, id }) => ({
											label: name,
											value: id.toString(),
										})
									),
								]}
							/>
						</>
					)}
					<Select
						value={tag}
						onChange={(val) => setAttributes({ tag: val })}
						label={__("Tag", "magazine-blocks")}
						options={[
							{ value: "all", label: __("All") },
							...(tags ?? [])?.map(({ name, id }) => ({
								label: name,
								value: id.toString(),
							})),
						]}
					/>
					<Select
						options={postTypeOptions ?? []}
						onChange={(val) => setAttributes({ postType: val })}
						value={postType}
						label={__("Post Type", "magazine-blocks")}
					/>
					<Slider
						label={__("Number of Posts", "magazine-blocks")}
						value={postCount}
						step={1}
						min={1}
						max={15}
						onChange={(val) => setAttributes({ postCount: val })}
					/>
					<Slider
						label={__("Offset", "magazine-blocks")}
						value={offset}
						step={1}
						min={0}
						max={15}
						onChange={(val) => setAttributes({ offset: val })}
					/>
				</Panel>
				<Panel title={__("Post Title", "magazine-blocks")}>
					<Toggle
						checked={enableTitle}
						onChange={() =>
							setAttributes({
								enableTitle: !enableTitle,
							})
						}
						label={__("Title", "magazine-blocks")}
					/>
					{enableTitle && (
						<Select
							options={[
								{
									label: __("H1", "magazine-blocks"),
									value: "h1",
								},
								{
									label: __("H2", "magazine-blocks"),
									value: "h2",
								},
								{
									label: __("H3", "magazine-blocks"),
									value: "h3",
								},
								{
									label: __("H4", "magazine-blocks"),
									value: "h4",
								},
								{
									label: __("H5", "magazine-blocks"),
									value: "h5",
								},
								{
									label: __("H6", "magazine-blocks"),
									value: "h6",
								},
							]}
							onChange={(val) =>
								setAttributes({ postTitleMarkup: val })
							}
							value={postTitleMarkup}
							label={__("HTML Markup", "magazine-blocks")}
						/>
					)}
				</Panel>
				<Panel title={__("Header Meta", "magazine-blocks")}>
					<Toggle
						checked={enableCategoryForHighlightPost}
						onChange={() =>
							setAttributes({
								enableCategoryForHighlightPost:
									!enableCategoryForHighlightPost,
							})
						}
						label={__(
							"Highlighted Post Category",
							"magazine-blocks"
						)}
					/>
					<Toggle
						checked={enableCategory}
						onChange={() =>
							setAttributes({
								enableCategory: !enableCategory,
							})
						}
						label={__("Post Box Category", "magazine-blocks")}
					/>
				</Panel>
				<Panel title={__("Meta", "magazine-blocks")}>
					{(enableDate || enableAuthor) && (
						<ToggleButtonGroup
							value={metaPosition}
							onChange={(v) => {
								setAttributes({ metaPosition: v });
							}}
							label={__("Position", "magazine-blocks")}
							resetAttributeKey="metaPosition"
							groupProps={{
								sx: {
									".chakra-button": {
										minH: "32px",
										svg: {
											w: 6,
											h: 6,
										},
									},
								},
							}}
						>
							<ToggleButton value="top">
								<LayoutAlignTop />
							</ToggleButton>
							<ToggleButton value="bottom">
								<LayoutAlignBottom />
							</ToggleButton>
						</ToggleButtonGroup>
					)}
					<Toggle
						checked={enableAuthor}
						onChange={() =>
							setAttributes({
								enableAuthor: !enableAuthor,
							})
						}
						label={__("Author", "magazine-blocks")}
					/>
					<Toggle
						checked={enableDate}
						onChange={() =>
							setAttributes({ enableDate: !enableDate })
						}
						label={__("Date", "magazine-blocks")}
					/>
					<Toggle
						checked={enableMetaSeparator}
						onChange={() =>
							setAttributes({
								enableMetaSeparator: !enableMetaSeparator,
							})
						}
						label={__("Separator", "magazine-blocks")}
					/>
					{enableMetaSeparator && (
						<Select
							options={[
								{
									label: __("None", "magazine-blocks"),
									value: "none",
								},
								{
									label: __("Circle", "magazine-blocks"),
									value: "circle",
								},
								{
									label: __("Square", "magazine-blocks"),
									value: "square",
								},
								{
									label: __(
										"Vertical Dash",
										"magazine-blocks"
									),
									value: "vertical-dash",
								},
								{
									label: __(
										"Horizontal Dash",
										"magazine-blocks"
									),
									value: "horizontal-dash",
								},
								{
									label: __(
										"Outline Circle",
										"magazine-blocks"
									),
									value: "outline-circle",
								},
							]}
							onChange={(val) =>
								setAttributes({ separatorType: val })
							}
							value={separatorType}
							label={__("Separator Type", "magazine-blocks")}
						/>
					)}
					<Toggle
						checked={enableIcon}
						onChange={() =>
							setAttributes({ enableIcon: !enableIcon })
						}
						label={__("Icon", "magazine-blocks")}
					/>
				</Panel>
				<Panel title={__("Excerpt", "magazine-blocks")}>
					<Toggle
						checked={enableExcerpt}
						onChange={() =>
							setAttributes({ enableExcerpt: !enableExcerpt })
						}
						label={__("Enable", "magazine-blocks")}
					/>
					{enableExcerpt && (
						<Slider
							label={__("Limit", "magazine-blocks")}
							value={excerptLimit}
							step={1}
							min={1}
							max={20}
							onChange={(val) =>
								setAttributes({ excerptLimit: val })
							}
						/>
					)}
				</Panel>
				<Panel title={__("Read More", "magazine-blocks")}>
					<Toggle
						checked={enableReadMore}
						onChange={() =>
							setAttributes({
								enableReadMore: !enableReadMore,
							})
						}
						label={__("Enable", "magazine-blocks")}
					/>
					{enableReadMore && (
						<Input
							value={readMoreText}
							label={__("Text", "magazine-blocks")}
							onChange={(val) =>
								setAttributes({ readMoreText: val })
							}
						/>
					)}
				</Panel>
			</GeneralInspectorControls>
			<StyleInspectorControls>
				<Panel title={__("General", "magazine-blocks")} initialOpen>
					<Slider
						onChange={(val: any) =>
							setAttributes({ columnGap: val })
						}
						label={__("Gap", "magazine-blocks")}
						units={["px", "em", "%"]}
						responsive={true}
						min={0}
						max={40}
						value={columnGap}
						resetAttributeKey="columnGap"
					/>
				</Panel>
				<Panel title={__("Thumbnail Image", "magazine-blocks")}>
					<Dimensions
						value={radius || {}}
						responsive
						label={__("Border Radius", "magazine-blocks")}
						defaultUnit="px"
						units={["px", "rem", "em", "%"]}
						onChange={(val) => setAttributes({ radius: val })}
						resetAttributeKey="radius"
					/>
				</Panel>
				<Panel title={__("Icon", "magazine-blocks")}>
					<Slider
						label={__("Size", "magazine-blocks")}
						min={0}
						max={50}
						value={iconSize}
						onChange={(val: any) =>
							setAttributes({ iconSize: val })
						}
						responsive
						units={["px", "em", "%"]}
					/>
					<PopoverDrawer
						label={__("Icon Color", "magazine-blocks")}
						trigger={(props) => (
							<PopoverDrawer.Trigger {...props}>
								<ZStack
									items={[
										{
											color: iconColor,
											id: "iconColor",
										},
									]}
								/>
							</PopoverDrawer.Trigger>
						)}
						closeOnFocusOutside
					>
						<Tabs position="relative">
							<TabList>
								<Tab>{__("Normal", "magazine-blocks")}</Tab>
							</TabList>

							<TabPanels>
								<TabPanel>
									<Reset
										saved={iconColor}
										resetKey="iconColor"
										onReset={(v) =>
											setAttributes({ iconColor: v })
										}
										buttonProps={{
											position: "absolute",
											top: "12px",
											right: "10px",
										}}
									></Reset>
									<ColorPicker
										value={iconColor}
										onChange={(v) =>
											setAttributes({ iconColor: v })
										}
									></ColorPicker>
								</TabPanel>
							</TabPanels>
						</Tabs>
					</PopoverDrawer>
					<PopoverDrawer label={__("Background", "magazine-blocks")}>
						<Tabs position="relative">
							<TabList>
								<Tab>{__("Normal", "magazine-blocks")}</Tab>
							</TabList>
							<TabPanels>
								<TabPanel>
									<Reset
										saved={iconBackground}
										resetKey="iconBackground"
										onReset={(v) => {
											setAttributes({
												iconBackground: v,
											});
										}}
										buttonProps={{
											position: "absolute",
											top: "12px",
											right: "10px",
										}}
									/>
									<Background
										value={iconBackground}
										onChange={(v) => {
											setAttributes({
												iconBackground: v,
											});
										}}
									/>
								</TabPanel>
							</TabPanels>
						</Tabs>
					</PopoverDrawer>
					<PopoverDrawer label={__("Border", "magazine-blocks")}>
						<Tabs position="relative">
							<TabList>
								<Tab>{__("Normal", "magazine-blocks")}</Tab>
							</TabList>
							<TabPanels>
								<TabPanel>
									<Reset
										saved={iconBorder}
										resetKey="iconBorder"
										onReset={(v) => {
											setAttributes({
												iconBorder: v,
											});
										}}
										buttonProps={{
											position: "absolute",
											top: "12px",
											right: "10px",
										}}
									/>
									<Border
										value={iconBorder}
										onChange={(v) => {
											setAttributes({
												iconBorder: v,
											});
										}}
										resetAttributeKey="iconBorder"
									/>
								</TabPanel>
							</TabPanels>
						</Tabs>
					</PopoverDrawer>
				</Panel>
				<Panel title={__("Post Content", "magazine-blocks")}>
					<Dimensions
						value={contentPadding}
						responsive
						label={__("Padding", "magazine-blocks")}
						type="padding"
						defaultUnit="px"
						units={["px", "rem", "em", "%"]}
						onChange={(val) =>
							setAttributes({ contentPadding: val })
						}
						resetAttributeKey="contentPadding"
					/>
					<Slider
						onChange={(val: any) =>
							setAttributes({ featuredImageWidth: val })
						}
						label={__("Width", "magazine-blocks")}
						units={["px", "em", "%"]}
						responsive={true}
						min={70}
						max={1000}
						value={featuredImageWidth}
						resetAttributeKey="featuredImageWidth"
					/>
					<Slider
						onChange={(val: any) =>
							setAttributes({ featuredImageHeight: val })
						}
						label={__("Height", "magazine-blocks")}
						units={["px", "em", "%"]}
						responsive={true}
						min={70}
						max={1000}
						value={featuredImageHeight}
						resetAttributeKey="featuredImageHeight"
					/>
				</Panel>
				{enableTitle && (
					<>
						<Panel
							title={__("Highlighted Post", "magazine-blocks")}
						>
							<PopoverDrawer
								label={__("Text Color", "magazine-blocks")}
								trigger={(props) => (
									<PopoverDrawer.Trigger {...props}>
										<ZStack
											items={[
												{
													color: highlightedPostTitleColor,
													id: "highlightedPostTitleColor",
												},
												{
													color: highlightedPostTitleHoverColor,
													id: "highlightedPostTitleHoverColor",
												},
											]}
										/>
									</PopoverDrawer.Trigger>
								)}
								closeOnFocusOutside
							>
								<Tabs position="relative">
									<TabList>
										<Tab>
											{__("Normal", "magazine-blocks")}
										</Tab>
										<Tab>
											{__("Hover", "magazine-blocks")}
										</Tab>
									</TabList>
									<TabPanels>
										<TabPanel>
											<Reset
												saved={
													highlightedPostTitleColor
												}
												resetKey="highlightedPostTitleColor"
												onReset={(v) => {
													setAttributes({
														highlightedPostTitleColor:
															v,
													});
												}}
												buttonProps={{
													position: "absolute",
													top: "12px",
													right: "10px",
												}}
											/>
											<ColorPicker
												value={
													highlightedPostTitleColor
												}
												onChange={(v) =>
													setAttributes({
														highlightedPostTitleColor:
															v,
													})
												}
											/>
										</TabPanel>
										<TabPanel>
											<Reset
												saved={
													highlightedPostTitleHoverColor
												}
												resetKey="highlightedPostTitleHoverColor"
												onReset={(v) => {
													setAttributes({
														highlightedPostTitleHoverColor:
															v,
													});
												}}
												buttonProps={{
													position: "absolute",
													top: "12px",
													right: "10px",
												}}
											/>
											<ColorPicker
												value={
													highlightedPostTitleHoverColor
												}
												onChange={(v) =>
													setAttributes({
														highlightedPostTitleHoverColor:
															v,
													})
												}
											/>
										</TabPanel>
									</TabPanels>
								</Tabs>
							</PopoverDrawer>
							<Typography
								value={highlightedPostTitleTypography}
								onChange={(val) =>
									setAttributes({
										highlightedPostTitleTypography: val,
									})
								}
							/>
							<PopoverDrawer
								label={__("Overlay", "magazine-blocks")}
							>
								<Tabs position="relative">
									<TabList>
										<Tab>
											{__("Normal", "magazine-blocks")}
										</Tab>
									</TabList>
									<TabPanels>
										<TabPanel>
											<Reset
												saved={
													highlightedPostBoxOverlay
												}
												resetKey="highlightedPostBoxOverlay"
												onReset={(v) => {
													setAttributes({
														highlightedPostBoxOverlay:
															v,
													});
												}}
												buttonProps={{
													position: "absolute",
													top: "12px",
													right: "10px",
												}}
											/>
											<Background
												value={
													highlightedPostBoxOverlay
												}
												onChange={(v) =>
													setAttributes({
														highlightedPostBoxOverlay:
															v,
													})
												}
											/>
										</TabPanel>
									</TabPanels>
								</Tabs>
							</PopoverDrawer>
							{presets === "style-3" && (
								<Dimensions
									value={highlightedPostContentPadding}
									responsive
									label={__("Padding", "magazine-blocks")}
									type="padding"
									defaultUnit="px"
									units={["px", "rem", "em", "%"]}
									onChange={(val) =>
										setAttributes({
											highlightedPostContentPadding: val,
										})
									}
									resetAttributeKey="highlightedPostContentPadding"
								/>
							)}
							<Slider
								onChange={(val: any) =>
									setAttributes({ highlightedPostWidth: val })
								}
								label={__("Width", "magazine-blocks")}
								units={["px", "em", "%"]}
								responsive={true}
								min={0}
								max={1000}
								resetAttributeKey="highlightedPostWidth"
								value={highlightedPostWidth}
							/>
							<Slider
								onChange={(val: any) =>
									setAttributes({
										highlightedPostHeight: val,
									})
								}
								label={__("Height", "magazine-blocks")}
								units={["px", "em", "%"]}
								responsive={true}
								min={70}
								max={1000}
								value={highlightedPostHeight}
								resetAttributeKey="highlightedPostHeight"
							/>
						</Panel>
						<Panel title={__("Post Box", "magazine-blocks")}>
							{presets !== "style-1" && (
								<PopoverDrawer
									label={__("Overlay", "magazine-blocks")}
								>
									<Tabs position="relative">
										<TabList>
											<Tab>
												{__(
													"Normal",
													"magazine-blocks"
												)}
											</Tab>
										</TabList>
										<TabPanels>
											<TabPanel>
												<Reset
													saved={postBoxOverlay}
													resetKey="postBoxOverlay"
													onReset={(v) => {
														setAttributes({
															postBoxOverlay: v,
														});
													}}
													buttonProps={{
														position: "absolute",
														top: "12px",
														right: "10px",
													}}
												/>
												<Background
													value={postBoxOverlay}
													onChange={(v) =>
														setAttributes({
															postBoxOverlay: v,
														})
													}
												/>
											</TabPanel>
										</TabPanels>
									</Tabs>
								</PopoverDrawer>
							)}
							<Slider
								onChange={(val: any) =>
									setAttributes({ postBoxWidth: val })
								}
								label={__("Width", "magazine-blocks")}
								units={["px", "em", "%"]}
								responsive={true}
								min={0}
								max={1000}
								value={postBoxWidth}
								resetAttributeKey="postBoxWidth"
							/>
						</Panel>
						<Panel title={__("Post Title", "magazine-blocks")}>
							<PopoverDrawer
								label={__("Text Color", "magazine-blocks")}
								trigger={(props) => (
									<PopoverDrawer.Trigger {...props}>
										<ZStack
											items={[
												{
													color: postTitleColor,
													id: "postTitleColor",
												},
												{
													color: postTitleHoverColor,
													id: "postTitleHoverColor",
												},
											]}
										/>
									</PopoverDrawer.Trigger>
								)}
								closeOnFocusOutside
							>
								<Tabs position="relative">
									<TabList>
										<Tab>
											{__("Normal", "magazine-blocks")}
										</Tab>
										<Tab>
											{__("Hover", "magazine-blocks")}
										</Tab>
									</TabList>
									<TabPanels>
										<TabPanel>
											<Reset
												saved={postTitleColor}
												resetKey="postTitleColor"
												onReset={(v) => {
													setAttributes({
														postTitleColor: v,
													});
												}}
												buttonProps={{
													position: "absolute",
													top: "12px",
													right: "10px",
												}}
											/>
											<ColorPicker
												value={postTitleColor}
												onChange={(v) =>
													setAttributes({
														postTitleColor: v,
													})
												}
											/>
										</TabPanel>
										<TabPanel>
											<Reset
												saved={postTitleHoverColor}
												resetKey="postTitleHoverColor"
												onReset={(v) => {
													setAttributes({
														postTitleHoverColor: v,
													});
												}}
												buttonProps={{
													position: "absolute",
													top: "12px",
													right: "10px",
												}}
											/>
											<ColorPicker
												value={postTitleHoverColor}
												onChange={(v) =>
													setAttributes({
														postTitleHoverColor: v,
													})
												}
											/>
										</TabPanel>
									</TabPanels>
								</Tabs>
							</PopoverDrawer>
							<Typography
								value={postTitleTypography}
								onChange={(val) =>
									setAttributes({ postTitleTypography: val })
								}
							/>
						</Panel>
					</>
				)}
				{(enableCategory || enableCategoryForHighlightPost) && (
					<Panel title={__("Header Meta", "magazine-blocks")}>
						<Typography
							value={headerMetaTypography}
							onChange={(val) =>
								setAttributes({ headerMetaTypography: val })
							}
						/>
						<PopoverDrawer
							label={__("Text Color", "magazine-blocks")}
							trigger={(props) => (
								<PopoverDrawer.Trigger {...props}>
									<ZStack
										items={[
											{
												color: categoryColor,
												id: "categoryColor",
											},
											{
												color: categoryHoverColor,
												id: "categoryHoverColor",
											},
										]}
									/>
								</PopoverDrawer.Trigger>
							)}
							closeOnFocusOutside
						>
							<Tabs position="relative">
								<TabList>
									<Tab>{__("Normal", "magazine-blocks")}</Tab>
									<Tab>{__("Hover", "magazine-blocks")}</Tab>
								</TabList>
								<TabPanels>
									<TabPanel>
										<Reset
											saved={categoryColor}
											resetKey="categoryColor"
											onReset={(v) => {
												setAttributes({
													categoryColor: v,
												});
											}}
											buttonProps={{
												position: "absolute",
												top: "12px",
												right: "10px",
											}}
										/>
										<ColorPicker
											value={categoryColor}
											onChange={(v) =>
												setAttributes({
													categoryColor: v,
												})
											}
										/>
									</TabPanel>
									<TabPanel>
										<Reset
											saved={categoryHoverColor}
											resetKey="categoryHoverColor"
											onReset={(v) => {
												setAttributes({
													categoryHoverColor: v,
												});
											}}
											buttonProps={{
												position: "absolute",
												top: "12px",
												right: "10px",
											}}
										/>
										<ColorPicker
											value={categoryHoverColor}
											onChange={(v) =>
												setAttributes({
													categoryHoverColor: v,
												})
											}
										/>
									</TabPanel>
								</TabPanels>
							</Tabs>
						</PopoverDrawer>
						<PopoverDrawer
							label={__("Background", "magazine-blocks")}
						>
							<Tabs position="relative">
								<TabList>
									<Tab>{__("Normal", "magazine-blocks")}</Tab>
									<Tab>{__("Hover", "magazine-blocks")}</Tab>
								</TabList>
								<TabPanels>
									<TabPanel>
										<Reset
											saved={categoryBackground}
											resetKey="categoryBackground"
											onReset={(v) => {
												setAttributes({
													categoryBackground: v,
												});
											}}
											buttonProps={{
												position: "absolute",
												top: "12px",
												right: "10px",
											}}
										/>
										<Background
											value={categoryBackground}
											onChange={(v) =>
												setAttributes({
													categoryBackground: v,
												})
											}
										/>
									</TabPanel>
									<TabPanel>
										<Reset
											saved={categoryHoverBackground}
											resetKey="categoryHoverBackground"
											onReset={(v) => {
												setAttributes({
													categoryHoverBackground: v,
												});
											}}
											buttonProps={{
												position: "absolute",
												top: "12px",
												right: "10px",
											}}
										/>
										<Background
											value={categoryHoverBackground}
											onChange={(v) =>
												setAttributes({
													categoryHoverBackground: v,
												})
											}
										/>
									</TabPanel>
								</TabPanels>
							</Tabs>
						</PopoverDrawer>
						<Dimensions
							value={categoryPadding}
							responsive
							label={__("Padding", "magazine-blocks")}
							type="padding"
							defaultUnit="px"
							units={["px", "rem", "em", "%"]}
							onChange={(val) =>
								setAttributes({ categoryPadding: val })
							}
						/>
						<Toggle
							checked={enableCategoryBorder}
							onChange={() =>
								setAttributes({
									enableCategoryBorder: !enableCategoryBorder,
								})
							}
							label={__("Border", "magazine-blocks")}
						/>
						{enableCategoryBorder && (
							<Tabs isFitted variant="unstyled">
								<TabList
									border="1px solid #E0E0E0"
									p="4px"
									borderRadius="2px"
								>
									<Tab
										_selected={{
											color: "white",
											bg: "#7E36F4",
											borderRadius: "2px",
										}}
									>
										Normal
									</Tab>
									<Tab
										_selected={{
											color: "white",
											bg: "#7E36F4",
											borderRadius: "2px",
										}}
									>
										Hover
									</Tab>
								</TabList>
								<TabPanels>
									<TabPanel p="16px 0">
										<Border
											value={categoryBorder}
											onChange={(v) => {
												setAttributes({
													categoryBorder: v,
												});
											}}
										/>
										<BoxShadow
											resetAttributeKey="categoryBoxShadow"
											value={categoryBoxShadow}
											onChange={(val) =>
												setAttributes({
													categoryBoxShadow: val,
												})
											}
										/>
									</TabPanel>
									<TabPanel p="16px 0">
										<Border
											value={categoryHoverBorder}
											onChange={(v) => {
												setAttributes({
													categoryHoverBorder: v,
												});
											}}
										/>
										<BoxShadow
											resetAttributeKey="categoryBoxShadowHover"
											value={categoryBoxShadowHover}
											onChange={(val) =>
												setAttributes({
													categoryBoxShadowHover: val,
												})
											}
										/>
									</TabPanel>
								</TabPanels>
							</Tabs>
						)}
					</Panel>
				)}
				{(enableAuthor || enableDate) && (
					<Panel title={__("Meta", "magazine-blocks")}>
						<PopoverDrawer
							label={__("Icon Color", "magazine-blocks")}
							trigger={(props) => (
								<PopoverDrawer.Trigger {...props}>
									<ZStack
										items={[
											{
												color: metaIconColor,
												id: "metaIconColor",
											},
										]}
									/>
								</PopoverDrawer.Trigger>
							)}
							closeOnFocusOutside
						>
							<Tabs position="relative">
								<TabList>
									<Tab>{__("Normal", "magazine-blocks")}</Tab>
								</TabList>
								<TabPanels>
									<TabPanel>
										<Reset
											saved={metaIconColor}
											resetKey="metaIconColor"
											onReset={(v) => {
												setAttributes({
													metaIconColor: v,
												});
											}}
											buttonProps={{
												position: "absolute",
												top: "12px",
												right: "10px",
											}}
										/>
										<ColorPicker
											value={metaIconColor}
											onChange={(v) =>
												setAttributes({
													metaIconColor: v,
												})
											}
										/>
									</TabPanel>
								</TabPanels>
							</Tabs>
						</PopoverDrawer>
						<PopoverDrawer
							label={__("Link Color", "magazine-blocks")}
							trigger={(props) => (
								<PopoverDrawer.Trigger {...props}>
									<ZStack
										items={[
											{
												color: metaLinkColor,
												id: "color",
											},
											{
												color: metaLinkHoverColor,
												id: "hoverColor",
											},
										]}
									/>
								</PopoverDrawer.Trigger>
							)}
							closeOnFocusOutside
						>
							<Tabs position="relative">
								<TabList>
									<Tab>{__("Normal", "magazine-blocks")}</Tab>
									<Tab>{__("Hover", "magazine-blocks")}</Tab>
								</TabList>
								<TabPanels>
									<TabPanel>
										<Reset
											saved={metaLinkColor}
											resetKey="metaLinkColor"
											onReset={(v) => {
												setAttributes({
													metaLinkColor: v,
												});
											}}
											buttonProps={{
												position: "absolute",
												top: "12px",
												right: "10px",
											}}
										/>
										<ColorPicker
											value={metaLinkColor}
											onChange={(v) =>
												setAttributes({
													metaLinkColor: v,
												})
											}
										/>
									</TabPanel>
									<TabPanel>
										<Reset
											saved={metaLinkHoverColor}
											resetKey="metaLinkHoverColor"
											onReset={(v) => {
												setAttributes({
													metaLinkHoverColor: v,
												});
											}}
											buttonProps={{
												position: "absolute",
												top: "12px",
												right: "10px",
											}}
										/>
										<ColorPicker
											value={metaLinkHoverColor}
											onChange={(v) =>
												setAttributes({
													metaLinkHoverColor: v,
												})
											}
										/>
									</TabPanel>
								</TabPanels>
							</Tabs>
						</PopoverDrawer>
						<PopoverDrawer
							label={__("Separator Color", "magazine-blocks")}
							trigger={(props) => (
								<PopoverDrawer.Trigger {...props}>
									<ZStack
										items={[
											{
												color: metaSeparatorColor,
												id: "metaSeparatorColor",
											},
										]}
									/>
								</PopoverDrawer.Trigger>
							)}
							closeOnFocusOutside
						>
							<Tabs position="relative">
								<TabList>
									<Tab>{__("Normal", "magazine-blocks")}</Tab>
								</TabList>
								<TabPanels>
									<TabPanel>
										<Reset
											saved={metaSeparatorColor}
											resetKey="metaSeparatorColor"
											onReset={(v) => {
												setAttributes({
													metaSeparatorColor: v,
												});
											}}
											buttonProps={{
												position: "absolute",
												top: "12px",
												right: "10px",
											}}
										/>
										<ColorPicker
											value={metaSeparatorColor}
											onChange={(v) =>
												setAttributes({
													metaSeparatorColor: v,
												})
											}
										/>
									</TabPanel>
								</TabPanels>
							</Tabs>
						</PopoverDrawer>
						<Typography
							value={metaTypography}
							onChange={(val) =>
								setAttributes({
									metaTypography: val,
								})
							}
						/>
					</Panel>
				)}
				{enableExcerpt && (
					<Panel title={__("Excerpt", "magazine-blocks")}>
						<PopoverDrawer
							label={__("Text Color", "magazine-blocks")}
							trigger={(props) => (
								<PopoverDrawer.Trigger {...props}>
									<ZStack
										items={[
											{
												color: excerptColor,
												id: "excerptColor",
											},
										]}
									/>
								</PopoverDrawer.Trigger>
							)}
							closeOnFocusOutside
						>
							<Tabs position="relative">
								<TabList>
									<Tab>{__("Normal", "magazine-blocks")}</Tab>
								</TabList>
								<TabPanels>
									<TabPanel>
										<Reset
											saved={excerptColor}
											resetKey="excerptColor"
											onReset={(v) => {
												setAttributes({
													excerptColor: v,
												});
											}}
											buttonProps={{
												position: "absolute",
												top: "12px",
												right: "10px",
											}}
										/>
										<ColorPicker
											value={excerptColor}
											onChange={(v) =>
												setAttributes({
													excerptColor: v,
												})
											}
										/>
									</TabPanel>
								</TabPanels>
							</Tabs>
						</PopoverDrawer>
						<Dimensions
							label={__("Margin", "magazine-blocks")}
							onChange={(v) =>
								setAttributes({ excerptMargin: v })
							}
							value={excerptMargin}
							responsive
							resetAttributeKey="excerptMargin"
						/>
					</Panel>
				)}
				{enableReadMore && (
					<Panel title={__("Read More", "magazine-blocks")}>
						<PopoverDrawer
							label={__("Text Color", "magazine-blocks")}
							trigger={(props) => (
								<PopoverDrawer.Trigger {...props}>
									<ZStack
										items={[
											{
												color: readMoreColor,
												id: "readMoreColor",
											},
											{
												color: readMoreHoverColor,
												id: "readMoreHoverColor",
											},
										]}
									/>
								</PopoverDrawer.Trigger>
							)}
							closeOnFocusOutside
						>
							<Tabs position="relative">
								<TabList>
									<Tab>{__("Normal", "magazine-blocks")}</Tab>
									<Tab>{__("Hover", "magazine-blocks")}</Tab>
								</TabList>
								<TabPanels>
									<TabPanel>
										<Reset
											saved={readMoreColor}
											resetKey="readMoreColor"
											onReset={(v) => {
												setAttributes({
													readMoreColor: v,
												});
											}}
											buttonProps={{
												position: "absolute",
												top: "12px",
												right: "10px",
											}}
										/>
										<ColorPicker
											value={readMoreColor}
											onChange={(v) =>
												setAttributes({
													readMoreColor: v,
												})
											}
										/>
									</TabPanel>
									<TabPanel>
										<Reset
											saved={readMoreHoverColor}
											resetKey="readMoreHoverColor"
											onReset={(v) => {
												setAttributes({
													readMoreHoverColor: v,
												});
											}}
											buttonProps={{
												position: "absolute",
												top: "12px",
												right: "10px",
											}}
										/>
										<ColorPicker
											value={readMoreHoverColor}
											onChange={(v) =>
												setAttributes({
													readMoreHoverColor: v,
												})
											}
										/>
									</TabPanel>
								</TabPanels>
							</Tabs>
						</PopoverDrawer>
						<PopoverDrawer
							label={__("Background", "magazine-blocks")}
						>
							<Tabs position="relative">
								<TabList>
									<Tab>{__("Normal", "magazine-blocks")}</Tab>
									<Tab>{__("Hover", "magazine-blocks")}</Tab>
								</TabList>
								<TabPanels>
									<TabPanel>
										<Reset
											saved={readMoreBackground}
											resetKey="readMoreBackground"
											onReset={(v) => {
												setAttributes({
													readMoreBackground: v,
												});
											}}
											buttonProps={{
												position: "absolute",
												top: "12px",
												right: "10px",
											}}
										/>
										<Background
											value={readMoreBackground}
											onChange={(v) =>
												setAttributes({
													readMoreBackground: v,
												})
											}
										/>
									</TabPanel>
									<TabPanel>
										<Reset
											saved={readMoreHoverBackground}
											resetKey="readMoreHoverBackground"
											onReset={(v) => {
												setAttributes({
													readMoreHoverBackground: v,
												});
											}}
											buttonProps={{
												position: "absolute",
												top: "12px",
												right: "10px",
											}}
										/>
										<Background
											value={readMoreHoverBackground}
											onChange={(v) =>
												setAttributes({
													readMoreHoverBackground: v,
												})
											}
										/>
									</TabPanel>
								</TabPanels>
							</Tabs>
						</PopoverDrawer>
						<Slider
							onChange={(val: any) =>
								setAttributes({ readMoreSpacing: val })
							}
							label={__("Spacing", "magazine-blocks")}
							units={["px", "em", "%"]}
							responsive={true}
							min={0}
							max={100}
							value={readMoreSpacing}
						/>
						<Dimensions
							value={readMorePadding}
							responsive
							label={__("Padding", "magazine-blocks")}
							type="margin"
							defaultUnit="px"
							units={["px", "rem", "em", "%"]}
							onChange={(val) =>
								setAttributes({ readMorePadding: val })
							}
						/>
						<Toggle
							checked={enableReadMoreBorder}
							onChange={() =>
								setAttributes({
									enableReadMoreBorder: !enableReadMoreBorder,
								})
							}
							label={__("Border", "magazine-blocks")}
						/>
						{enableReadMoreBorder && (
							<Tabs isFitted variant="unstyled">
								<TabList
									border="1px solid #E0E0E0"
									p="4px"
									borderRadius="2px"
								>
									<Tab
										_selected={{
											color: "white",
											bg: "#7E36F4",
											borderRadius: "2px",
										}}
									>
										Normal
									</Tab>
									<Tab
										_selected={{
											color: "white",
											bg: "#7E36F4",
											borderRadius: "2px",
										}}
									>
										Hover
									</Tab>
								</TabList>
								<TabPanels>
									<TabPanel p="16px 0">
										<Border
											value={readMoreBorder}
											onChange={(v) => {
												setAttributes({
													readMoreBorder: v,
												});
											}}
										/>
										<BoxShadow
											resetAttributeKey="readMoreBoxShadow"
											value={readMoreBoxShadow}
											onChange={(val) =>
												setAttributes({
													readMoreBoxShadow: val,
												})
											}
										/>
									</TabPanel>
									<TabPanel p="16px 0">
										<Border
											value={readMoreHoverBorder}
											onChange={(v) => {
												setAttributes({
													readMoreHoverBorder: v,
												});
											}}
										/>
										<BoxShadow
											resetAttributeKey="readMoreBoxShadowHover"
											value={readMoreBoxShadowHover}
											onChange={(val) =>
												setAttributes({
													readMoreBoxShadowHover: val,
												})
											}
										/>
									</TabPanel>
								</TabPanels>
							</Tabs>
						)}
					</Panel>
				)}
			</StyleInspectorControls>
			<AdvancedInspectorControls>
				<AdvancedControls
					blockMargin={blockMargin}
					setAttributes={setAttributes}
					blockPadding={blockPadding}
				/>
				<ResponsiveControls
					{...{
						hideOnDesktop,
						hideOnTablet,
						hideOnMobile,
						setAttributes,
					}}
				/>
			</AdvancedInspectorControls>
		</>
	);
};

export default InspectorControls;
